import { useState, useEffect } from 'react';
import { publicSiteApi, type PublicSite } from '../lib/publicSiteApi';
import { AdmissionsEnquiryForm } from '../components/AdmissionsEnquiryForm';

/**
 * THE PUBLIC SCHOOL PAGE — the mini-site a prospective parent sees at the school's address, before
 * any login. Everything shown is either school-edited content or a NewsPost the school explicitly
 * marked public; a named student never appears here unless someone chose to showcase them.
 *
 * Rendered at the tenant root by the Gate when the school has enabled its public page. If it hasn't,
 * the Gate falls through to the normal app/login (this component is never reached).
 */
export function PublicSitePage({ site }: { site: PublicSite }) {
  const brand = site.primaryColor || '#0E1A2B';
  const accent = site.accentColor || '#C9A15A';
  const heroImg = site.heroImageUrl ? publicSiteApi.fileUrl(site.heroImageUrl) : null;
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', color: '#1f2937', background: '#fff' }}>
      {/* ---- top bar ---- */}
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
        {site.logoUrl && <img src={site.logoUrl} alt="" style={{ width: 40, height: 40, objectFit: 'contain' }} />}
        <strong style={{ fontSize: 18, color: brand }}>{site.schoolName}</strong>
        <nav style={{ marginLeft: 'auto', display: 'flex', gap: 18, fontSize: 14 }}>
          {site.showAchievements && <a href="#achievements" style={{ color: '#374151', textDecoration: 'none' }}>Achievements</a>}
          {site.showAbout && <a href="#about" style={{ color: '#374151', textDecoration: 'none' }}>About</a>}
          {site.showGallery && <a href="#gallery" style={{ color: '#374151', textDecoration: 'none' }}>Gallery</a>}
          {site.showContact && <a href="#contact" style={{ color: '#374151', textDecoration: 'none' }}>Contact</a>}
          <a href="/login" style={{ color: accent, fontWeight: 600, textDecoration: 'none' }}>Login</a>
        </nav>
      </header>

      {/* ---- hero ---- */}
      <section style={{
        position: 'relative', padding: '96px 24px', textAlign: 'center', color: '#fff',
        background: heroImg
          ? `linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.55)), url("${heroImg}") center/cover`
          : `linear-gradient(135deg, ${brand}, ${shade(brand)})`,
      }}>
        <h1 style={{ fontSize: 44, fontWeight: 800, margin: 0, maxWidth: 820, marginInline: 'auto', lineHeight: 1.15 }}>
          {site.heroHeadline || site.schoolName}
        </h1>
        {site.heroHeadline && <p style={{ fontSize: 18, marginTop: 12, opacity: .95 }}>{site.schoolName}</p>}
        {site.admissionsOpen && (
          <button onClick={() => setEnquiryOpen(true)} style={{
            display: 'inline-block', marginTop: 28, padding: '12px 28px', borderRadius: 999,
            background: accent, color: '#fff', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: 16,
          }}>
            {site.admissionsNote || 'Admissions open — enquire now'}
          </button>
        )}
      </section>

      {/* ---- stats strip ---- */}
      {site.showStats && site.stats.length > 0 && (
        <section style={{ background: brand, color: '#fff', padding: '28px 24px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: `repeat(${Math.min(site.stats.length, 4)}, 1fr)`, gap: 16, textAlign: 'center' }}>
            {site.stats.map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 30, fontWeight: 800, color: accent }}>{s.value}</div>
                <div style={{ fontSize: 13, opacity: .9 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- achievements ---- */}
      {site.showAchievements && site.achievements.length > 0 && (
        <section id="achievements" style={{ padding: '64px 24px', maxWidth: 1080, margin: '0 auto' }}>
          <SectionTitle accent={accent}>Our achievements</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginTop: 28 }}>
            {site.achievements.map((a, i) => {
              const img = a.imageUrls[0] ? publicSiteApi.fileUrl(a.imageUrls[0]) : null;
              return (
                <article key={i} style={{ border: '1px solid #eee', borderRadius: 14, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
                  {img && <img src={img} alt="" style={{ width: '100%', height: 168, objectFit: 'cover' }} />}
                  <div style={{ padding: 16 }}>
                    {a.highlight && (
                      <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 999, background: accent, color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                        {a.highlight}
                      </span>
                    )}
                    <h3 style={{ margin: '4px 0', fontSize: 17, color: brand }}>{a.title}</h3>
                    {a.studentName && <div style={{ fontSize: 13, color: '#6b7280' }}>{a.studentName}</div>}
                    {a.body && <p style={{ fontSize: 14, color: '#4b5563', marginTop: 6 }}>{a.body}</p>}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* ---- about ---- */}
      {site.showAbout && (site.aboutBody || site.boardsOffered) && (
        <section id="about" style={{ background: '#f9fafb', padding: '64px 24px' }}>
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <SectionTitle accent={accent}>{site.aboutTitle || `About ${site.schoolName}`}</SectionTitle>
            {site.aboutBody && <p style={{ fontSize: 16, lineHeight: 1.8, color: '#374151', marginTop: 20, whiteSpace: 'pre-wrap' }}>{site.aboutBody}</p>}
            <div style={{ display: 'flex', gap: 28, marginTop: 20, flexWrap: 'wrap', fontSize: 14, color: '#6b7280' }}>
              {site.establishedYear && <div><strong style={{ color: brand }}>Established</strong> · {site.establishedYear}</div>}
              {site.boardsOffered && <div><strong style={{ color: brand }}>Boards</strong> · {site.boardsOffered}</div>}
            </div>
          </div>
        </section>
      )}

      {/*
        ═══ OUR INSTITUTIONS ═══

        ⚠ RENDERED ONLY WHEN THE SERVER SENT MORE THAN ONE. The site is still one page per trust —
        that is the right shape for somewhere that markets itself under one name people already
        know. What it could not do was say what is INSIDE: a parent could read
        "CBSE · ICSE · State · PU · Degree" in the about section and not tell which board belonged
        to which school, where either was, or whether the one they wanted was admitting.

        ⚠ Array.isArray, not truthiness. An older server does not send this key at all, and
        `undefined.length` would take the whole public page down for every visitor — on the one
        screen in the product that a stranger sees before they have any reason to trust it.

        ⚠ Grouped by TOWN when the trust spans more than one, because for a parent choosing a school
        the town is the first filter and the name is the second.
      */}
      {Array.isArray(site.institutions) && site.institutions.length > 1 && (
        <section id="institutions" style={{ padding: '64px 24px', maxWidth: 1080, margin: '0 auto' }}>
          <SectionTitle accent={accent}>Our institutions</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 28 }}>
            {site.institutions.map((i) => (
              <div key={i.id} style={{
                border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, background: '#fff',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                <div style={{ fontWeight: 700, color: brand, fontSize: 16 }}>{i.name}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {i.typeLabel}{i.boardLabel ? ` · ${i.boardLabel}` : ''}
                </div>
                {/* ⚠ The town, not the campus name alone — "Harihara Campus" means nothing to a
                    parent who does not already know the trust. */}
                {(i.city || i.campusName) && (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{i.city || i.campusName}</div>
                )}
                {i.blurb && (
                  <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: '4px 0 0' }}>{i.blurb}</p>
                )}
                <div style={{ marginTop: 'auto', paddingTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {/*
                    ⚠ ADMISSIONS SHOWN PER INSTITUTION. A school and a PU college under one trust
                    open in different months; one trust-wide badge would tell half the families
                    the wrong thing, and the half it misleads are the ones who go elsewhere.
                  */}
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                    background: i.admissionsOpen ? '#ecfdf5' : '#f3f4f6',
                    color: i.admissionsOpen ? '#047857' : '#6b7280',
                  }}>
                    {i.admissionsOpen ? 'Admissions open' : 'Admissions closed'}
                  </span>
                  {i.phone && <a href={`tel:${i.phone}`} style={{ fontSize: 13, color: brand }}>{i.phone}</a>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- gallery ---- */}
      {site.showGallery && site.gallery.length > 0 && (
        <section id="gallery" style={{ padding: '64px 24px', maxWidth: 1080, margin: '0 auto' }}>
          <SectionTitle accent={accent}>Gallery</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginTop: 28 }}>
            {site.gallery.map((g, i) => (
              <img key={i} src={publicSiteApi.fileUrl(g.url)} alt={g.caption || ''} title={g.caption || ''}
                style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 10, border: '1px solid #eee' }} />
            ))}
          </div>
        </section>
      )}

      {/* ---- contact / footer ---- */}
      {site.showContact && (
        <section id="contact" style={{ background: brand, color: '#fff', padding: '56px 24px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
            <div>
              <h3 style={{ color: accent, marginTop: 0 }}>{site.schoolName}</h3>
              {site.addressLine && <p style={{ opacity: .9, fontSize: 14, whiteSpace: 'pre-wrap' }}>{site.addressLine}</p>}
            </div>
            <div style={{ fontSize: 14, lineHeight: 2 }}>
              {site.contactPhone && <div>📞 {site.contactPhone}</div>}
              {site.contactEmail && <div>✉️ <a href={`mailto:${site.contactEmail}`} style={{ color: '#fff' }}>{site.contactEmail}</a></div>}
              {site.websiteUrl && <div>🌐 <a href={site.websiteUrl} style={{ color: '#fff' }} target="_blank" rel="noreferrer">Website</a></div>}
            </div>
            <div style={{ fontSize: 14, lineHeight: 2 }}>
              {site.facebookUrl && <div><a href={site.facebookUrl} style={{ color: '#fff' }} target="_blank" rel="noreferrer">Facebook</a></div>}
              {site.instagramUrl && <div><a href={site.instagramUrl} style={{ color: '#fff' }} target="_blank" rel="noreferrer">Instagram</a></div>}
              {site.youtubeUrl && <div><a href={site.youtubeUrl} style={{ color: '#fff' }} target="_blank" rel="noreferrer">YouTube</a></div>}
              {site.admissionsOpen && <div><button onClick={() => setEnquiryOpen(true)} style={{ background: 'none', border: 'none', color: accent, fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>Admissions enquiry</button></div>}
            </div>
          </div>
          {site.mapUrl && (
            <div style={{ maxWidth: 960, margin: '24px auto 0', textAlign: 'center' }}>
              <a href={site.mapUrl} target="_blank" rel="noreferrer" style={{ color: accent, fontSize: 14 }}>📍 View on map</a>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, opacity: .6 }}>
            © {new Date().getFullYear()} {site.schoolName} · Powered by Eduvora
          </div>
        </section>
      )}
      {enquiryOpen && <AdmissionsEnquiryForm brand={brand} accent={accent} onClose={() => setEnquiryOpen(false)} />}
    </div>
  );
}

function SectionTitle({ children, accent }: { children: React.ReactNode; accent: string }) {
  return (
    <h2 style={{ fontSize: 30, fontWeight: 800, textAlign: 'center', position: 'relative', paddingBottom: 14 }}>
      {children}
      <span style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 56, height: 4, borderRadius: 2, background: accent }} />
    </h2>
  );
}

// darken a hex colour a touch for the hero gradient fallback
function shade(hex: string): string {
  try {
    const n = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, ((n >> 16) & 255) - 30), g = Math.max(0, ((n >> 8) & 255) - 30), b = Math.max(0, (n & 255) - 30);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  } catch { return hex; }
}

/** Loader wrapper used by the Gate: fetches the public site; renders it or signals "not enabled". */
export function PublicSiteGate({ onNoSite }: { onNoSite: () => void }) {
  const [site, setSite] = useState<PublicSite | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    publicSiteApi.get()
      .then((s) => { if (s) setSite(s); else onNoSite(); })
      .catch(() => onNoSite())
      .finally(() => setChecked(true));
  }, [onNoSite]);

  if (!checked) return null;           // brief: decide before painting
  if (!site) return null;              // not enabled → Gate shows the app instead
  return <PublicSitePage site={site} />;
}
